import { useState, useEffect } from 'react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import apiClient from '../../services/api';

interface User {
  id: number;
  email: string;
  name: string;
  role: string;
  balance: number;
  status: string;
}

export function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/admin/users');
      setUsers(response.data.data || response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyUser = async (userId: number) => {
    try {
      await apiClient.post(`/admin/users/${userId}/verify`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to verify user:', error);
    }
  };

  const handleToggleStatus = async (userId: number, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'suspended' : 'active';
    if (!confirm(`确定要将用户状态改为 ${newStatus === 'suspended' ? '已暂停' : '活跃'} 吗？`)) {
      return;
    }
    try {
      await apiClient.put(`/admin/users/${userId}/status`, { status: newStatus });
      fetchUsers();
    } catch (error) {
      console.error('Failed to update user status:', error);
      alert('更新状态失败');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      deleted: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      active: '活跃',
      suspended: '已暂停',
      deleted: '已删除',
    };
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">用户管理</h1>
        <Button onClick={fetchUsers}>刷新</Button>
      </div>

      <Card>
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">邮箱</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">姓名</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">角色</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">余额</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">状态</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm">{user.id}</td>
                    <td className="py-3 px-4 text-sm">{user.email}</td>
                    <td className="py-3 px-4 text-sm">{user.name || '-'}</td>
                    <td className="py-3 px-4 text-sm">{user.role}</td>
                    <td className="py-3 px-4 text-sm">${user.balance?.toFixed(2) || '0.00'}</td>
                    <td className="py-3 px-4">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex space-x-2">
                        {user.status !== 'active' && (
                          <Button size="sm" onClick={() => handleVerifyUser(user.id)}>
                            验证
                          </Button>
                        )}
                        {user.status !== 'deleted' && (
                          <Button
                            size="sm"
                            variant={user.status === 'active' ? 'secondary' : 'primary'}
                            onClick={() => handleToggleStatus(user.id, user.status)}
                          >
                            {user.status === 'active' ? '暂停' : '启用'}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
